import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Check,
  ChevronDown,
  ChevronRight,
  KeyRound,
  Languages,
  Moon,
  Sun,
  UserRound,
} from "lucide-react";
import {
  getAccounts,
  getActiveAccount,
  getActiveConsumer,
  getConsumers,
  getUser,
  isAuthConfigured,
  setActiveAccountId,
  setActiveConsumerId,
} from "../../libs/auth";
import { toggleTheme } from "../../libs/theme";
import { supportedLanguages } from "../../i18n";
import { cn } from "@/lib/utils";
import { useAuthVersion } from "@/hooks/use-auth-version";
import { useTheme } from "@/hooks/use-theme";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";

export type Crumb = {
  label: string
  /** Bỏ trống ở crumb cuối (trang hiện tại). */
  to?: string
};

type HeaderProps = {
  crumbs: Crumb[]
  /** Header có shadow khi vùng nội dung đã cuộn. */
  scrolled?: boolean
};

export function Header({ crumbs, scrolled }: HeaderProps) {
  return (
    <header
      className={cn(
        "bg-background/95 sticky top-0 z-40 h-16 shrink-0 border-b backdrop-blur-lg",
        scrolled ? "shadow-sm" : "shadow-none"
      )}
    >
      <div className="flex h-full items-center gap-3 px-4 sm:gap-4">
        <SidebarTrigger variant="outline" className="max-md:scale-125" />
        <Separator orientation="vertical" className="h-6!" />
        <Breadcrumbs crumbs={crumbs} />
        <div className="ms-auto flex items-center gap-2">
          <AccountSwitcher />
          <ConsumerSwitcher />
          <LanguageSwitcher />
          <ThemeSwitch />
        </div>
      </div>
    </header>
  );
}

function Breadcrumbs({ crumbs }: { crumbs: Crumb[] }) {
  return (
    <nav
      aria-label="breadcrumb"
      className="flex min-w-0 items-center gap-1.5 text-sm"
    >
      {crumbs.map((c, i) => {
        const last = i === crumbs.length - 1;
        return (
          <span key={i} className="flex min-w-0 items-center gap-1.5">
            {i > 0 && (
              <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
            )}
            {c.to && !last ? (
              <Link
                to={c.to}
                className="text-muted-foreground hover:text-foreground truncate transition-colors"
              >
                {c.label}
              </Link>
            ) : (
              <span className="truncate font-semibold">{c.label}</span>
            )}
          </span>
        );
      })}
    </nav>
  );
}

function shortId(id: string) {
  return id.length <= 12 ? id : `${id.slice(0, 8)}…`;
}

/**
 * Nhãn của nút chọn trong header: luôn 1 dòng, cắt "…" khi dài; ẩn trên
 * mobile (nút đã có aria-label). Không dùng `sm:not-sr-only` vì utility đó
 * đặt `white-space: normal` làm tên dài bị xuống dòng.
 */
function TriggerLabel({ children }: { children: React.ReactNode }) {
  return <span className="hidden min-w-0 truncate sm:block">{children}</span>;
}

/** Chỉ hiện khi user có từ 2 account trở lên. */
function AccountSwitcher() {
  const { t } = useTranslation();
  useAuthVersion();
  const accounts = getAccounts();
  const active = getActiveAccount();
  if (!isAuthConfigured() || !getUser() || accounts.length < 2) return null;

  const label = active?.name || active?.email || t("Chọn tài khoản");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 max-w-56 gap-1.5"
          aria-label={label}
          title={label}
        >
          <UserRound className="h-4 w-4" />
          <TriggerLabel>{label}</TriggerLabel>
          <ChevronDown className="text-muted-foreground h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>{t("Chọn tài khoản")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {accounts.map((a) => (
          <DropdownMenuItem key={a.id} onClick={() => setActiveAccountId(a.id)}>
            <div className="grid min-w-0 flex-1">
              <span className="truncate">{a.name || a.id}</span>
              {a.email && a.name !== a.email && (
                <span className="text-muted-foreground truncate text-xs">
                  {a.email}
                </span>
              )}
            </div>
            {a.id === active?.id && <Check className="ms-auto h-4 w-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ConsumerSwitcher() {
  const { t } = useTranslation();
  useAuthVersion();
  const consumers = getConsumers();
  const active = getActiveConsumer();
  if (!isAuthConfigured() || !getUser() || consumers.length === 0) return null;

  const label = active
    ? `${active.name || t("Consumer")} - ${shortId(active.id)}`
    : t("Chọn consumer");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 max-w-64 gap-1.5"
          aria-label={label}
          title={active ? `${active.name || t("Consumer")} - ${active.id}` : label}
        >
          <KeyRound className="h-4 w-4" />
          <TriggerLabel>{label}</TriggerLabel>
          <ChevronDown className="text-muted-foreground h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>{t("Chọn consumer")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {consumers.map((c) => (
          <DropdownMenuItem key={c.id} onClick={() => setActiveConsumerId(c.id)}>
            <span className="truncate">{c.name || c.id}</span>
            {c.id === active?.id && <Check className="ms-auto h-4 w-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const LANG_FLAG: Record<string, string> = { vi: "🇻🇳", en: "🇺🇸" };

function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          aria-label={t("Đổi ngôn ngữ")}
        >
          <Languages className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {Object.entries(supportedLanguages).map(([code, name]) => (
          <DropdownMenuItem key={code} onClick={() => i18n.changeLanguage(code)}>
            <span>{LANG_FLAG[code] ?? "🌐"}</span>
            {name}
            {(i18n.language || "").startsWith(code) && (
              <Check className="ms-auto h-4 w-4" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ThemeSwitch() {
  const { t } = useTranslation();
  const theme = useTheme();
  const label = theme === "dark" ? t("Chế độ sáng") : t("Chế độ tối");
  return (
    <Button
      variant="ghost"
      size="icon"
      className="size-8"
      onClick={() => toggleTheme()}
      aria-label={label}
      title={label}
    >
      {theme === "dark" ? (
        <Sun className="h-4 w-4" />
      ) : (
        <Moon className="h-4 w-4" />
      )}
    </Button>
  );
}
